import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { GlassCard } from '../components/ui/glass-card';
import { apiClient, axiosInstance } from '../services/apiClient';
import { ResetPasswordRequest } from '../generated/api-client';
import toast from 'react-hot-toast';
import PasswordRequirementsChecklist from '../components/PasswordRequirementsChecklist';
import { describeMissingRequirements, validatePassword } from '../utils/passwordPolicy';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [token, setToken] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      setError('Token di reset non fornito');
      return;
    }
    setToken(tokenParam);
  }, [searchParams]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error('Token mancante');
      return;
    }

    if (!password.trim()) {
      toast.error('Inserisci la password');
      return;
    }
    if (!passwordConfirm.trim()) {
      toast.error('Conferma la password');
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('Le password non coincidono');
      return;
    }
    const missingRequirements = describeMissingRequirements(password);
    if (missingRequirements) {
      toast.error(missingRequirements);
      return;
    }

    setLoading(true);

    try {
      const request = new ResetPasswordRequest({
        token,
        new_password: password,
        new_password_confirm: passwordConfirm,
      });
      await apiClient.reset_password_auth_reset_password_post(request);
      setSuccess(true);
      toast.success('Password resettata con successo!');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.detail || 'Errore nel reset della password';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = theme.palette.primary.main;
  const primaryLightColor = theme.palette.primary.light;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        overflow: 'auto',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* sfere sfumate */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '192px',
          height: '192px',
          borderRadius: '50%',
          backgroundColor: 'rgba(0, 161, 156, 0.1)',
          filter: 'blur(60px)',
          zIndex: 1,
          animation: 'pulse 3s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 0.5 },
            '50%': { opacity: 0.8 },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '288px',
          height: '288px',
          borderRadius: '50%',
          backgroundColor: 'rgba(146, 214, 213, 0.1)',
          filter: 'blur(60px)',
          zIndex: 1,
          animation: 'pulse 3s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 0.5 },
            '50%': { opacity: 0.8 },
          },
        }}
      />

      <GlassCard
        hover={false}
        style={{
          width: '100%',
          maxWidth: '28rem',
          padding: '1rem',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Button
          variant="text"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/login')}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            color: theme.palette.text.primary,
            minWidth: 'auto',
          }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
            EDAI
          </Typography>
        </Box>

        <Typography
          variant="h5"
          component="h1"
          align="center"
          sx={{
            mb: 2,
            fontWeight: 600,
            background: `linear-gradient(135deg, ${primaryColor}, ${primaryLightColor})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Reset Password
        </Typography>
        <Typography
          align="center"
          sx={{
            mb: 4,
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
          }}
        >
          Inserisci la nuova password per il tuo account.
        </Typography>

        {error && !token ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="error">{error}</Alert>
            <Button
              onClick={() => navigate('/login')}
              variant="outlined"
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderColor: primaryColor,
                color: primaryColor,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  bgcolor: `${primaryColor}10`,
                },
              }}
            >
              Torna al Login
            </Button>
          </Box>
        ) : success ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="success" sx={{ bgcolor: `${primaryColor}15` }}>
              Password resettata con successo! Verrai reindirizzato al login...
            </Alert>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleResetPassword}
            sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              id="password"
              label="Nuova Password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              fullWidth
              required
              variant="outlined"
              slotProps={{ input: { sx: { borderRadius: '8px' } } }}
            />

            {password.length > 0 && <PasswordRequirementsChecklist password={password} />}

            <TextField
              id="passwordConfirm"
              label="Conferma Password"
              type="password"
              value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)}
              placeholder="••••••••"
              fullWidth
              required
              variant="outlined"
              slotProps={{ input: { sx: { borderRadius: '8px' } } }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={loading || !validatePassword(password).valid || password !== passwordConfirm}
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${primaryLightColor}, ${primaryColor})`,
                transition: 'filter 0.2s ease-in-out',
                '&:hover': {
                  filter: 'brightness(0.9)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="text"
                onClick={() => navigate('/login')}
                sx={{
                  color: primaryLightColor,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                  fontSize: '0.875rem',
                }}
              >
                Torna al Login
              </Button>
            </Box>
          </Box>
        )}
      </GlassCard>
    </Box>
  );
};

export default ResetPasswordPage;
